"use client";

import { useMemo, useState } from "react";
import type {
  AppLocale,
  FunnelPackageTier,
  FunnelStageConfig,
  FunnelTemplateId,
  FunnelWizardState,
  PackageAdviserDecision,
  PackageRecommendation,
  Product,
  RegionalSettings,
} from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import {
  resolvePlannerPackageAdvice,
  toPackageRecommendationState,
  type PlannerPricingSnapshot,
} from "@/lib/funnel-planner/package-adviser";
import type { FunnelAdvisoryFlags } from "@/lib/funnel-planner/advisory-flags";
import { PackageAdviserPanel } from "../ui/PackageAdviserPanel";
import { funnelText, productTypeLabel } from "@/lib/funnel-planner/i18n";
import {
  joinAuditTicketGroups,
  STEP_THREE_CTA_TICKETS,
  STEP_THREE_DISCLOSURE_PANEL_TICKETS,
  STEP_THREE_FIELD_INPUT_TICKETS,
  STEP_THREE_PRODUCT_COUNT_CONTROL_SUPPORT_TICKETS,
  STEP_THREE_PRODUCT_OFFER_CARD_SUPPORT_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";
import { ProductCard } from "../ui/ProductCard";
import { track } from "@/lib/posthog/events";

type ProductLibraryStepProps = {
  audience: FunnelWizardState["audience"];
  packageRecommendation: PackageRecommendation;
  pricingSnapshot: PlannerPricingSnapshot | null;
  advisoryFlags: FunnelAdvisoryFlags;
  /** True when viewing someone else's shared plan: the advisory panel is read-only. */
  advisoryReadOnly?: boolean;
  products: Product[];
  stages: FunnelStageConfig[];
  templateId: FunnelTemplateId | null;
  onPackageRecommendationChange: (packageRecommendation: PackageRecommendation) => void;
  onProductsChange: (products: Product[]) => void;
  content: FunnelPlannerContent;
  locale: AppLocale;
  regionalSettings: RegionalSettings;
};

function createDefaultProduct(content: FunnelPlannerContent): Product {
  return {
    id: Math.random().toString(36).slice(2),
    name: funnelText(content, "productDefaultName", "Core Offer"),
    productType: "online_course",
    priceType: "one_time",
    price: 497,
    cogsPercent: 8,
    refundRatePercent: 8,
    merchantFeePercent: 2.9,
    merchantFixedFee: 0.3,
    hasTrial: false,
    isUpsell: false,
    usageBased: false,
    guaranteeType: "none",
    offerRole: "core",
    attachedStageIds: [],
    checkoutType: "one_step",
    productSalesSplitPct: 100,
    orderBumpEnabled: false,
    orderBumpTakeRatePct: 20,
    downsellTakeRatePct: 12,
    fulfillmentCostAmount: 0,
    fulfillmentCostType: "none",
  };
}

const MAX_PRODUCTS = 5;
const PRODUCT_COUNT_REVEAL_TICKET =
  "P2-311001-section-step-03-product-count-control-before-after-ai-answer-reveal-";
const STEP_THREE_OFFER_ASSEMBLY_CTA_TICKETS = [
  "P1-238001-cta-step-03-button-planner-action-025-offer-assembly-mini-cta-brief",
  "P1-239001-cta-step-03-button-planner-action-026-offer-assembly-mini-cta-brief",
  "P1-240001-cta-step-03-button-planner-action-027-offer-assembly-mini-cta-brief",
  "P1-241001-cta-step-03-button-planner-action-028-offer-assembly-mini-cta-brief",
  "P1-242001-cta-step-03-button-planner-action-029-offer-assembly-mini-cta-brief",
  "P1-243001-cta-step-03-button-planner-action-030-offer-assembly-mini-cta-brief",
  "P1-244001-cta-step-03-button-planner-action-031-offer-assembly-mini-cta-brief",
  "P1-245001-cta-step-03-button-planner-action-032-offer-assembly-mini-cta-brief",
  "P1-246001-cta-step-03-button-planner-action-033-offer-assembly-mini-cta-brief",
  "P1-247001-cta-step-03-button-planner-action-034-offer-assembly-mini-cta-brief",
  "P1-248001-cta-step-03-button-planner-action-035-offer-assembly-mini-cta-brief",
  "P1-249001-cta-step-03-button-planner-action-036-offer-assembly-mini-cta-brief",
  "P1-250001-cta-step-03-button-planner-action-037-offer-assembly-mini-cta-brief",
  "P1-251001-cta-step-03-button-planner-action-038-offer-assembly-mini-cta-brief",
  "P1-252001-cta-step-03-button-planner-action-039-offer-assembly-mini-cta-brief",
] as const;
const STEP_THREE_PRODUCT_ACTION_TICKETS = [
  ...STEP_THREE_OFFER_ASSEMBLY_CTA_TICKETS,
  ...STEP_THREE_CTA_TICKETS,
] as const;
const PRODUCT_LIBRARY_AUDIT_TICKETS = joinAuditTicketGroups(
  PRODUCT_COUNT_REVEAL_TICKET,
  STEP_THREE_FIELD_INPUT_TICKETS,
  STEP_THREE_DISCLOSURE_PANEL_TICKETS,
  STEP_THREE_PRODUCT_COUNT_CONTROL_SUPPORT_TICKETS,
  STEP_THREE_PRODUCT_OFFER_CARD_SUPPORT_TICKETS,
);
const READINESS_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";

const SUGGESTION_CHIPS = [
  { label: "Online Course", productType: "online_course" as const },
  { label: "Membership", productType: "membership" as const },
  { label: "Coaching", productType: "coaching" as const },
] as const;

function offerAssemblyCtaTicketsForProductAction(actionIndex: number, actionCount: number): string {
  return STEP_THREE_PRODUCT_ACTION_TICKETS
    .filter((_, ticketIndex) => ticketIndex % Math.max(actionCount, 1) === actionIndex)
    .join(";");
}

function productOfferAssemblyCtaAttributes(ticketGroup: string, active: boolean) {
  return {
    "data-funnel-planner-offer-assembly-cta": "true",
    "data-funnel-planner-step-three-offer-assembly-cta": "true",
    "data-audit-ticket": ticketGroup,
    "data-tier-one-family": "offer_assembly_mini_cta_brief",
    "data-gamification-current-state": active ? "complete_result" : "active_inprogress",
    "data-gamification-reduced-motion-static": "supported",
    "data-gamification-supported-states": READINESS_SUPPORTED_STATES,
    "data-readiness-ready-count": active ? 1 : 0,
    "data-readiness-total-count": 1,
    "data-readiness-tier": active ? "brief_piece_ready" : "brief_piece_available",
    "data-blockers-bucket": active ? "none" : "one",
  } as const;
}

export function ProductLibraryStep({
  audience,
  packageRecommendation,
  pricingSnapshot,
  advisoryFlags,
  advisoryReadOnly = false,
  products,
  stages,
  templateId,
  onPackageRecommendationChange,
  onProductsChange,
  content,
  locale,
  regionalSettings,
}: ProductLibraryStepProps) {
  const packageAdvice = useMemo(
    () =>
      resolvePlannerPackageAdvice({
        audience,
        templateId,
        products,
        regionalSettings,
        pricingSnapshot,
        decision: packageRecommendation.decision,
        overrideTier: packageRecommendation.selectedPackageId,
      }),
    [
      audience,
      packageRecommendation.decision,
      packageRecommendation.selectedPackageId,
      pricingSnapshot,
      products,
      regionalSettings,
      templateId,
    ],
  );

  function handlePackageDecision(decision: PackageAdviserDecision, tier?: FunnelPackageTier | null) {
    // Read-only is enforced in the handler, not only by the `disabled` attribute
    // the panel sets. A DOM attribute is the right affordance but the wrong
    // barrier: it stops user-initiated events on a native control and nothing
    // else, so a shared-plan visitor's decision must be refused here too. This
    // matches the guard SeasonalScenarioPanel already applies.
    if (advisoryReadOnly) return;

    onPackageRecommendationChange(
      toPackageRecommendationState(
        resolvePlannerPackageAdvice({
          audience,
          templateId,
          products,
          regionalSettings,
          pricingSnapshot,
          decision,
          overrideTier: tier ?? null,
        }),
      ),
    );
    // NL-041 analytics (gap #10). Categorical only: the decision and the chosen
    // tier identifier, never a price or range value.
    if (decision === "accepted" || decision === "overridden" || decision === "skipped") {
      track("funnel_planner_advisory_package_decided", {
        template_id: templateId ?? undefined,
        locale,
        decision,
        selected_tier: tier ?? undefined,
      });
    }
  }

  const [expandedIndex, setExpandedIndex] = useState<number | null>(products.length === 0 ? null : 0);
  const [chipHover, setChipHover] = useState<string | null>(null);
  const emptyProductActionCount = SUGGESTION_CHIPS.length + 1;

  function handleAdd() {
    if (products.length >= MAX_PRODUCTS) return;
    const next = [...products, createDefaultProduct(content)];
    onProductsChange(next);
    setExpandedIndex(next.length - 1);
  }

  function handleAddWithType(productType: Product["productType"]) {
    if (products.length >= MAX_PRODUCTS) return;
    const base = createDefaultProduct(content);
    const next = [...products, { ...base, productType }];
    onProductsChange(next);
    setExpandedIndex(next.length - 1);
  }

  function handleChange(index: number, updated: Product) {
    const next = [...products];
    next[index] = updated;
    onProductsChange(next);
  }

  function handleRemove(index: number) {
    const next = products.filter((_, i) => i !== index);
    onProductsChange(next);
    setExpandedIndex(next.length === 0 ? null : Math.min(expandedIndex ?? 0, next.length - 1));
  }

  function handleMoveUp(index: number) {
    if (index === 0) return;
    const next = [...products];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onProductsChange(next);
    setExpandedIndex(index - 1);
  }

  function handleMoveDown(index: number) {
    if (index === products.length - 1) return;
    const next = [...products];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onProductsChange(next);
    setExpandedIndex(index + 1);
  }

  const hasProducts = products.length > 0;

  return (
    <div
      data-funnel-planner-product-count-control="true"
      data-funnel-planner-product-offer-cards="true"
      data-audit-ticket={PRODUCT_LIBRARY_AUDIT_TICKETS}
      data-tier-one-family="before_after_section_reveal"
      data-gamification-current-state={hasProducts ? "complete_result" : "active_inprogress"}
      data-gamification-reduced-motion-static="supported"
      data-gamification-supported-states={READINESS_SUPPORTED_STATES}
      data-readiness-ready-count={hasProducts ? 1 : 0}
      data-readiness-total-count={1}
      data-readiness-tier={hasProducts ? "product_count_ready" : "product_count_needed"}
      data-blockers-bucket={hasProducts ? "none" : "one"}
      data-gamification-count-up-real-numbers="true"
      data-gamification-real-number-source="planner_product_count"
      data-gamification-real-number-count={products.length}
      data-gamification-real-number-total={MAX_PRODUCTS}
      style={{ display: "flex", flexDirection: "column", gap: 20 }}
    >
      <span className="sr-only" role="status" aria-live="polite">
        {hasProducts
          ? funnelText(content, "gamificationProductCountReady", "Product count ready for offer review")
          : funnelText(content, "gamificationProductCountWaiting", "Product count waiting for at least one offer")}
      </span>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>
        {funnelText(content, "productLibraryTitle", "Your Products")}
      </h2>

      {products.length === 0 && (
        <div style={{
          borderRadius: 8,
          border: "1px solid #92400e40",
          background: "rgba(251,191,36,0.08)",
          padding: "10px 16px",
          fontSize: 13,
          color: "#fbbf24",
        }}>
          {funnelText(content, "productEmptyWarning", "Add at least one product to unlock revenue projections.")}
        </div>
      )}

      {/* Empty state */}
      {products.length === 0 ? (
        <div style={{
          borderRadius: 12,
          border: "2px dashed #334155",
          background: "rgba(255,255,255,0.03)",
          padding: "40px 24px",
          textAlign: "center",
        }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#475569"
            strokeWidth={1.5}
            style={{ margin: "0 auto 12px" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16 3H8a2 2 0 00-2 2v2h12V5a2 2 0 00-2-2z"
            />
          </svg>
          <p style={{ fontSize: 13, fontWeight: 500, color: "#94a3b8", marginBottom: 4 }}>
            {funnelText(content, "productEmptyTitle", "No products added yet")}
          </p>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 12, marginTop: 0 }}>
            {funnelText(content, "productEmptyBody", "Start with your core offer - for example: an online course, SaaS subscription, or physical product.")}
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginBottom: 16 }}>
            {SUGGESTION_CHIPS.map((chip, index) => {
              const ticketGroup = offerAssemblyCtaTicketsForProductAction(index, emptyProductActionCount);
              return (
              <button
                key={chip.productType}
                type="button"
                {...productOfferAssemblyCtaAttributes(ticketGroup, false)}
                onClick={() => handleAddWithType(chip.productType)}
                aria-label={funnelText(content, "productAddTypeAria", "Add {type} product", {
                  type: productTypeLabel(content, chip.productType, chip.label),
                })}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  borderRadius: 20,
                  border: "1px solid #334155",
                  background: chipHover === chip.productType ? "rgba(37,99,235,0.10)" : "rgba(255,255,255,0.04)",
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 500,
                  color: chipHover === chip.productType ? "#93c5fd" : "#94a3b8",
                  cursor: "pointer",
                  minHeight: 44,
                  outline: "none",
                  transition: "color 0.15s, background 0.15s",
                  boxShadow: chipHover === chip.productType ? "0 0 0 2px #3b82f6" : "none",
                }}
                onMouseEnter={() => setChipHover(chip.productType)}
                onMouseLeave={() => setChipHover(null)}
                onFocus={() => setChipHover(chip.productType)}
                onBlur={() => setChipHover(null)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                {productTypeLabel(content, chip.productType, chip.label)}
                <span
                  aria-hidden="true"
                  style={{
                    borderRadius: 999,
                    border: "1px solid rgba(96,165,250,0.35)",
                    background: "rgba(37,99,235,0.10)",
                    color: "#bfdbfe",
                    fontSize: 9,
                    fontWeight: 800,
                    padding: "1px 6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {content.gamificationOfferAssemblyCtaScopeThis as string}
                </span>
                <span className="sr-only" role="status" aria-live="polite">
                  {content.gamificationOfferAssemblyCtaEmptyStatus as string}
                </span>
              </button>
              );
            })}
          </div>
          <button
            type="button"
            {...productOfferAssemblyCtaAttributes(
              offerAssemblyCtaTicketsForProductAction(SUGGESTION_CHIPS.length, emptyProductActionCount),
              false,
            )}
            onClick={handleAdd}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              borderRadius: 8,
              background: "#2563eb",
              padding: "14px 20px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 1px 4px rgba(37,99,235,0.3)",
              minHeight: 44,
              outline: "none",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = "#3b82f6"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = "#2563eb"; }}
            onFocus={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 0 2px #3b82f6"; }}
            onBlur={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 1px 4px rgba(37,99,235,0.3)"; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            {funnelText(content, "productAddFirstButton", "Add your first product")}
            <span className="sr-only" role="status" aria-live="polite">
              {content.gamificationOfferAssemblyCtaEmptyStatus as string}
            </span>
          </button>
        </div>
      ) : (
        <>
          {/* Product cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {products.map((product, i) => {
              const isExpanded = expandedIndex === i;
              return (
                <div key={product.id} style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 4 }}>
                      <button
                        type="button"
                        onClick={() => handleMoveUp(i)}
                        disabled={i === 0}
                        title={funnelText(content, "productMoveUpTitle", "Move up")}
                        aria-label={funnelText(content, "productMoveUpAria", "Move {product} up", { product: product.name })}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 44,
                          height: 44,
                          borderRadius: 6,
                          border: "1px solid #1e293b",
                          background: "transparent",
                          color: i === 0 ? "#334155" : "#94a3b8",
                          cursor: i === 0 ? "not-allowed" : "pointer",
                          padding: 0,
                          opacity: i === 0 ? 0.4 : 1,
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l6 6a1 1 0 01-1.414 1.414L10 5.414 4.707 10.707A1 1 0 013.293 9.293l6-6A1 1 0 0110 3z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(i)}
                        disabled={i === products.length - 1}
                        title={funnelText(content, "productMoveDownTitle", "Move down")}
                        aria-label={funnelText(content, "productMoveDownAria", "Move {product} down", { product: product.name })}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 44,
                          height: 44,
                          borderRadius: 6,
                          border: "1px solid #1e293b",
                          background: "transparent",
                          color: i === products.length - 1 ? "#334155" : "#94a3b8",
                          cursor: i === products.length - 1 ? "not-allowed" : "pointer",
                          padding: 0,
                          opacity: i === products.length - 1 ? 0.4 : 1,
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 17a1 1 0 01-.707-.293l-6-6a1 1 0 011.414-1.414L10 14.586l5.293-5.293a1 1 0 011.414 1.414l-6 6A1 1 0 0110 17z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <div style={{ flex: 1 }}>
                      <ProductCard
                        product={product}
                        index={i}
                        expanded={isExpanded}
                        onExpandToggle={() => setExpandedIndex(isExpanded ? null : i)}
                        inputTabIndex={isExpanded ? 0 : -1}
                        onChange={(updated) => handleChange(i, updated)}
                        onRemove={() => handleRemove(i)}
                        content={content}
                        locale={locale}
                        regionalSettings={regionalSettings}
                        stages={stages}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add product button — always rendered; disabled at limit */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              type="button"
              {...productOfferAssemblyCtaAttributes(STEP_THREE_OFFER_ASSEMBLY_CTA_TICKETS.join(";"), products.length > 0)}
              onClick={handleAdd}
              disabled={products.length >= MAX_PRODUCTS}
              title={products.length >= MAX_PRODUCTS
                ? funnelText(content, "productMaxTitle", "Maximum {max} products reached", { max: MAX_PRODUCTS })
                : undefined}
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                borderRadius: 12,
                border: `2px dashed ${products.length >= MAX_PRODUCTS ? "#1e293b" : "#334155"}`,
                padding: "14px 16px",
                fontSize: 13,
                fontWeight: 500,
                color: products.length >= MAX_PRODUCTS ? "#334155" : "#64748b",
                background: "transparent",
                cursor: products.length >= MAX_PRODUCTS ? "not-allowed" : "pointer",
                opacity: products.length >= MAX_PRODUCTS ? 0.5 : 1,
                minHeight: 44,
                outline: "none",
              }}
              onMouseEnter={e => {
                if (products.length >= MAX_PRODUCTS) return;
                const b = e.currentTarget as HTMLButtonElement;
                b.style.borderColor = "#3b82f6";
                b.style.color = "#3b82f6";
              }}
              onMouseLeave={e => {
                if (products.length >= MAX_PRODUCTS) return;
                const b = e.currentTarget as HTMLButtonElement;
                b.style.borderColor = "#334155";
                b.style.color = "#64748b";
              }}
              onFocus={e => {
                if (products.length >= MAX_PRODUCTS) return;
                (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 0 2px #3b82f6";
              }}
              onBlur={e => {
                (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              {products.length >= MAX_PRODUCTS
                ? funnelText(content, "productAddLimitButton", "Add Product ({count}/{max} - limit reached)", {
                    count: products.length,
                    max: MAX_PRODUCTS,
                  })
                : funnelText(content, "productAddButton", "Add Product")}
              <span
                aria-hidden="true"
                style={{
                  borderRadius: 999,
                  border: "1px solid rgba(16,185,129,0.45)",
                  background: "rgba(16,185,129,0.12)",
                  color: "#a7f3d0",
                  fontSize: 9,
                  fontWeight: 800,
                  padding: "1px 6px",
                  whiteSpace: "nowrap",
                }}
              >
                {content.gamificationOfferAssemblyCtaSelected as string}
              </span>
              <span className="sr-only" role="status" aria-live="polite">
                {content.gamificationOfferAssemblyCtaSelected as string}
              </span>
            </button>
            {products.length > 0 && products.length < MAX_PRODUCTS && (
              <span style={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>
                {funnelText(content, "productCountLabel", "{count} of {max} added", {
                  count: products.length,
                  max: MAX_PRODUCTS,
                })}
              </span>
            )}
            {products.length >= MAX_PRODUCTS && (
              <span style={{ fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
                {funnelText(content, "productMaxReached", "Maximum of {max} products reached", { max: MAX_PRODUCTS })}
              </span>
            )}
          </div>
        </>
      )}

      {advisoryFlags.packageRecommendationEnabled && (
        <PackageAdviserPanel
          advice={packageAdvice}
          approximate={pricingSnapshot?.approximate ?? false}
          content={content}
          onDecision={handlePackageDecision}
          readOnly={advisoryReadOnly}
        />
      )}

      {/* Tip box */}
      <div style={{
        borderRadius: 8,
        border: "1px solid #1e40af40",
        background: "rgba(37,99,235,0.07)",
        padding: "10px 16px",
        fontSize: 13,
        color: "#93c5fd",
      }}>
        <span style={{ fontWeight: 600 }}>{funnelText(content, "productTipPrefix", "Tip:")}</span>{" "}
        {funnelText(content, "productTipBody", "Add your core offer first - its revenue is the projection baseline and upsell take-rate is calculated against it. Add upsells or order bumps afterwards.")}
      </div>
    </div>
  );
}
